import React, { useState, createContext, useEffect, useContext } from "react";
import axios from "axios";
import useLocalStorage from "../hooks/useLocalStorage";

interface AuthContextInterface {
  userData: any;
  setUserData: any;
  login?: any;
  logout?: any;
  isLoggedIn: () => boolean;
  errorMessage?: string | null;
}

const initialValue: AuthContextInterface = {
  setUserData: () => {},
  userData: null,
  login: () => {},
  logout: () => {},
  isLoggedIn: () => false,
};

export const AuthContext: React.Context<AuthContextInterface> =
  createContext<AuthContextInterface>(initialValue);

export const AuthProvider = ({ children }: React.PropsWithChildren) => {
  const [userData, setUserData, removeUserData] = useLocalStorage<any>(
    "userDataWMSAdmin",
    null
  );
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    axios.defaults.headers.common[
      "Authorization"
    ] = `Bearer ${userData?.accessToken}`;
    axios.defaults.baseURL = process.env.REACT_APP_API_URL;
  }, [userData]);

  const isLoggedIn = () => userData !== null && userData?.accessToken !== null;

  const login = (email: string, password: string) => {
    setErrorMessage(null);
    return axios
      .post(`${process.env.REACT_APP_API_URL}/auth/login`, {
        email,
        password,
      })
      .then((res) => {
        console.log(res.data);
        if (res.data?.accessToken && res.data?.user?.isAdmin) {
          setUserData(res.data);
        } else if (res.data?.errors) {
          setErrorMessage(res.data?.errors);
        }
        return res;
      })
      .catch((error) => {
        console.log("AuthProvider -> error", error.response);
        setErrorMessage(error.response.data.message);
        return error;
      });
  };

  const logout = () => {
    removeUserData();
  };

  return (
    <AuthContext.Provider
      value={{
        setUserData,
        userData,
        login,
        logout,
        isLoggedIn,
        errorMessage,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = (): AuthContextInterface => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within AuthProvider");
  }
  return context;
};

export const AuthConsumer = AuthContext.Consumer;
